import { api } from './axios';

export interface FeeCollectionByType {
  feeTypeId: number;
  feeTypeName: string | null;
  receiptCount: number;
  amount: string;
}

export interface FeeCollectionByDate {
  date: string;
  receiptCount: number;
  amount: string;
}

export interface FeeCollectionByMethod {
  method: string;
  receiptCount: number;
  amount: string;
}

export interface FeeCollectionByCollector {
  userId: number | null;
  collector: string;
  receiptCount: number;
  amount: string;
}

export interface FeeCollectionByClassSection {
  classSectionId: number | null;
  className: string;
  sectionName: string;
  receiptCount: number;
  amount: string;
}

export interface FeeCollectionSummary {
  from: string;
  to: string;
  grossAmount: string;
  reversedAmount: string;
  netAmount: string;
  receiptCount: number;
  byType: FeeCollectionByType[];
  byDate: FeeCollectionByDate[];
  byMethod: FeeCollectionByMethod[];
  byCollector: FeeCollectionByCollector[];
  byClassSection: FeeCollectionByClassSection[];
}

export interface ConcessionByFeeType {
  feeTypeId: number;
  feeTypeName: string | null;
  invoiceCount: number;
  gross: string;
  discount: string;
  waived: string;
  concession: string;
}

export interface ConcessionByDiscountType {
  discountTypeId: number | null;
  discountTypeName: string;
  studentCount: number;
  amount: string;
}

export interface WaiverRow {
  invoiceId: number;
  enrollmentId: number;
  studentName: string;
  registrationNumber: string | null;
  className: string | null;
  sectionName: string | null;
  feeTypeName: string | null;
  periodMonth: string | null;
  amount: string;
  reason: string | null;
  approvedBy: string | null;
  waivedAt: string | null;
}

export interface ConcessionReport {
  from: string;
  to: string;
  grossBilled: string;
  totalDiscount: string;
  totalWaived: string;
  totalConcession: string;
  netReceivable: string;
  concessionPct: string;
  invoiceCount: number;
  byFeeType: ConcessionByFeeType[];
  byDiscountType: ConcessionByDiscountType[];
  waivers: WaiverRow[];
}

export interface CollectionTrendMonth {
  month: string;
  billed: string;
  collected: string;
  waived: string;
  outstanding: string;
  collectionPct: string;
}

export interface CollectionTrend {
  academicYearId: number;
  months: CollectionTrendMonth[];
  totalBilled: string;
  totalCollected: string;
  totalWaived: string;
  totalOutstanding: string;
  collectionPct: string;
}

export interface FeeTypeYieldRow {
  feeTypeId: number;
  feeTypeName: string;
  frequency: string | null;
  expected: string;
  billed: string;
  collected: string;
  outstanding: string;
  collectionPct: string;
}

export interface FeeTypeYield {
  academicYearId: number;
  rows: FeeTypeYieldRow[];
  totalExpected: string;
  totalBilled: string;
  totalCollected: string;
  totalOutstanding: string;
}

export interface ChronicAbsenteeRow {
  studentId: number;
  registrationNumber: string;
  studentName: string;
  enrollmentId: number;
  classSectionId: number;
  classSectionName: string | null;
  absent: number;
  halfDay: number;
  workingDays: number;
  absencePct: number;
}

export interface ChronicAbsenteeReport {
  from: string;
  to: string;
  workingDays: number;
  thresholdPct: number;
  rows: ChronicAbsenteeRow[];
}

export interface AgingRow {
  classSectionId: number;
  className: string;
  sectionName: string;
  studentCount: number;
  current: string;
  days0to30: string;
  days31to60: string;
  days61to90: string;
  over90: string;
  total: string;
}

export interface ReceivablesAging {
  asOf: string;
  academicYearId: number;
  current: string;
  days0to30: string;
  days31to60: string;
  days61to90: string;
  over90: string;
  grandTotal: string;
  rows: AgingRow[];
}

export interface CashBookRow {
  date: string;
  category: string;
  particulars: string;
  reference: string | null;
  account: string | null;
  moneyIn: string | null;
  moneyOut: string | null;
  balance: string;
}

export interface CashBookCategoryTotal {
  category: string;
  moneyIn: string;
  moneyOut: string;
}

export interface CashBookReport {
  from: string;
  to: string;
  bankAccountId: number | null;
  accountName: string | null;
  openingBalance: string;
  totalIn: string;
  totalOut: string;
  closingBalance: string;
  byCategory: CashBookCategoryTotal[];
  rows: CashBookRow[];
}

export interface BackdatedReceiptRow {
  receiptId: number;
  receiptNumber: number;
  paymentDate: string;
  enteredAt: string;
  daysBack: number;
  amount: string;
  paymentMethod: string | null;
  account: string | null;
  studentName: string;
  enteredById: number | null;
  enteredBy: string;
  reversed: boolean;
  afterHandover: boolean;
}

export interface BackdatedReceiptReport {
  minDaysBack: number;
  count: number;
  totalAmount: string;
  afterHandoverCount: number;
  afterHandoverAmount: string;
  rows: BackdatedReceiptRow[];
}

export const reportsApi = {
  backdatedReceipts: (minDaysBack: number) =>
    api
      .get<BackdatedReceiptReport>('/reports/backdated-receipts', { params: { minDaysBack } })
      .then((r) => r.data),

  cashBook: (from: string, to: string, bankAccountId?: number) =>
    api
      .get<CashBookReport>('/reports/cash-book', { params: { from, to, bankAccountId } })
      .then((r) => r.data),

  feeCollection: (from: string, to: string) =>
    api.get<FeeCollectionSummary>('/reports/fee-collection', { params: { from, to } }).then((r) => r.data),

  chronicAbsentees: (params: {
    classSectionId?: number;
    academicYearId?: number;
    from: string;
    to: string;
    thresholdPct: number;
  }) =>
    api.get<ChronicAbsenteeReport>('/reports/chronic-absentees', { params }).then((r) => r.data),

  receivablesAging: (params: { academicYearId?: number; asOf?: string }) =>
    api.get<ReceivablesAging>('/reports/receivables-aging', { params }).then((r) => r.data),

  concessions: (from: string, to: string) =>
    api.get<ConcessionReport>('/reports/concessions', { params: { from, to } }).then((r) => r.data),

  collectionTrend: (params: { academicYearId?: number } = {}) =>
    api.get<CollectionTrend>('/reports/collection-trend', { params }).then((r) => r.data),

  feeTypeYield: (params: { academicYearId?: number } = {}) =>
    api.get<FeeTypeYield>('/reports/fee-type-yield', { params }).then((r) => r.data),
};
